// M18 Zeitplan-Editor: Wochenansicht (Mo–So × 0–24 h) aus derselben Auswertung wie die Engine (@disploraq/schedule),
// Regel-Liste, Prioritätsauswahl 50/75/90 (Expertenmodus: freie Zahl), Konfliktanzeige, Simulation per Datumswahl.
import {
  addDays, candidatesAt, fromLocal, simulate, toLocal, validateRule, WEEKDAYS,
  type ScheduleRule as EngineRule, type ScheduleSpec, type Segment,
} from '@disploraq/schedule';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { can, qk, useLocations, useMe, usePlaylists, useSchedule } from '../api/queries';
import type { Playlist, RuleType, ScheduleDetail, ScheduleRule } from '../api/types';
import { PendingBanner, SubmitDialog, useApprovalRequired } from '../components/Approval';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Confirm, Field, PageHead, Spinner } from '../components/ui';
import { PALETTE, WEEKDAY_BITS } from '../lib';
import { openPreview } from '../preview/open';
import { AssignmentsCard } from './Schedules';

type Rule = ScheduleRule & { key: string };
let seq = 0;
const PRESETS = [50, 75, 90];

export function toEngineRule(r: Rule): EngineRule {
  return {
    id: r.key, playlist: r.playlistId, priority: r.priority, type: r.ruleType, enabled: r.enabled,
    weekdays: r.ruleType === 'weekly' ? WEEKDAYS.filter((_, i) => ((r.weekdays ?? 0) & WEEKDAY_BITS[i]!) !== 0) : null,
    timeFrom: r.timeFrom, timeUntil: r.timeUntil, dateFrom: r.dateFrom, dateUntil: r.ruleType === 'single_date' ? r.dateFrom : r.dateUntil,
  };
}

const isoDate = (d: { year: number; month: number; day: number }) => `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
const parseIso = (s: string) => { const [y, m, d] = s.split('-').map(Number); return { year: y!, month: m!, day: d! }; };

/** Montag der Woche, in der `date` liegt. */
function mondayOf(date: string) {
  const d = parseIso(date);
  const wd = (new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay() + 6) % 7;
  return addDays(d, -wd);
}

export function ScheduleEditorPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: s, isLoading } = useSchedule(id);
  const { data: playlists = [] } = usePlaylists();
  const { data: locations = [] } = useLocations();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const manage = can(me, 'schedules.manage');
  const [name, setName] = useState('');
  const [fallback, setFallback] = useState<string | null>(null);
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [version, setVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expert, setExpert] = useState(false);
  const [del, setDel] = useState(false);
  // Freigabe-Workflow: Änderungen einreichen statt speichern
  const approval = useApprovalRequired();
  const [submitOpen, setSubmitOpen] = useState(false);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Zurich';
  const [tz, setTz] = useState(browserTz);
  const today = isoDate(toLocal(Date.now(), tz));
  const [week, setWeek] = useState(today);

  useEffect(() => {
    if (s && rules === null) {
      setName(s.name);
      setFallback(s.fallbackPlaylistId);
      setRules(s.rules.map((r) => ({ ...r, key: r.id ?? `n${++seq}` })));
      setVersion(s.rowVersion);
      if (s.rules.some((r) => !PRESETS.includes(r.priority))) setExpert(true);
    }
  }, [s, rules]);

  const color = useMemo(() => {
    const ids = [...new Set([...(rules ?? []).map((r) => r.playlistId), fallback ?? ''])].filter(Boolean);
    return (pid: string | null) => (pid ? PALETTE[ids.indexOf(pid) % PALETTE.length] ?? '#64748b' : '#64748b');
  }, [rules, fallback]);

  const spec: ScheduleSpec = useMemo(() => ({ fallbackPlaylist: fallback, rules: (rules ?? []).map(toEngineRule) }), [rules, fallback]);
  const monday = mondayOf(week);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    const start = fromLocal(d, 0, tz);
    const end = fromLocal(addDays(d, 1), 0, tz);
    return { d, start, end };
  }), [monday.year, monday.month, monday.day, tz]);
  const segments: Segment[] = useMemo(() => {
    try {
      return simulate(spec, days[0]!.start, days[6]!.end, tz);
    } catch {
      return [];
    }
  }, [spec, days, tz]);
  const conflicts = useMemo(() => {
    const out = new Map<string, { winner: string; losers: Set<string> }>();
    for (const seg of segments) {
      if (seg.source !== 'rule' || !seg.ruleId) continue;
      const c = candidatesAt(spec, seg.from, tz);
      if (c.length < 2) continue;
      const e = out.get(seg.ruleId) ?? { winner: seg.ruleId, losers: new Set<string>() };
      c.slice(1).forEach((x) => e.losers.add(x.id));
      out.set(seg.ruleId, e);
    }
    return [...out.values()];
  }, [segments, spec, tz]);

  if (isLoading || !s || !rules) return <Spinner />;

  const update = (fn: (r: Rule[]) => Rule[]) => { setRules((cur) => fn(cur ?? [])); setDirty(true); };
  const patch = (key: string, p: Partial<Rule>) => update((l) => l.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const addRule = () => update((l) => [...l, {
    key: `n${++seq}`, playlistId: playlists[0]?.id ?? '', priority: 50, ruleType: 'weekly', weekdays: 31, dateFrom: null, dateUntil: null,
    timeFrom: '08:00', timeUntil: '18:00', enabled: true,
  }]);
  const ruleNo = (key: string) => rules.findIndex((r) => r.key === key) + 1;
  const plName = (pid: string | null) => playlists.find((p: Playlist) => p.id === pid)?.name ?? '–';
  const issues = rules.flatMap((r) => validateRule(toEngineRule(r)));

  const buildBody = () => ({
    name: name.trim(), fallbackPlaylistId: fallback,
    rules: rules.map((r) => ({
      playlistId: r.playlistId, priority: r.priority, ruleType: r.ruleType, enabled: r.enabled,
      weekdays: r.ruleType === 'weekly' ? r.weekdays : null,
      dateFrom: r.ruleType === 'weekly' ? null : r.dateFrom, dateUntil: r.ruleType === 'date_range' ? r.dateUntil : null,
      timeFrom: r.timeFrom || null, timeUntil: r.timeUntil || null,
    })),
  });
  const save = async (): Promise<boolean> => {
    setBusy(true);
    try {
      const body = buildBody();
      const res = await api.put<ScheduleDetail>(`/schedules/${id}`, body, { ifMatch: version });
      setVersion(res.rowVersion);
      setRules(res.rules.map((r) => ({ ...r, key: r.id ?? `n${++seq}` })));
      setDirty(false);
      qc.setQueryData([...qk.schedule(id), me?.activeTenantId ?? 'none'], res);
      await qc.invalidateQueries({ queryKey: qk.schedules });
      toast.show(t('common.saved'));
      return true;
    } catch (e) {
      toast.error(e);
      return false;
    } finally {
      setBusy(false);
    }
  };
  // M20: Vorschau mit Zeitpunkt-Simulation; ungespeicherte, gültige Änderungen vorher speichern
  const preview = () => openPreview('schedule', id, manage && !approval && dirty && name.trim() && !issues.length ? async () => {
    if (!(await save())) throw new Error('save failed');
  } : undefined, { tz }).catch(() => {});

  const hours = [0, 3, 6, 9, 12, 15, 18, 21];
  const wdNames = t('schedules.weekdaysShort', { returnObjects: true }) as string[];
  const tzs = [...new Set([browserTz, ...locations.map((l) => l.timezone)])];

  return (
    <>
      <PageHead
        title={manage ? <input aria-label={t('common.name')} value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }}
          style={{ fontSize: 22, fontWeight: 700, padding: '4px 8px', maxWidth: '100%' }} /> : s.name}
        sub={t('schedules.editorSub')}
        actions={<div className="row">
          <Link className="btn ghost" to="/schedules"><Icon name="left" />{t('common.back')}</Link>
          {manage && <button className="btn ghost danger" onClick={() => setDel(true)}><Icon name="trash" />{t('common.delete')}</button>}
          <button className="btn" onClick={() => void preview()} title={t('preview.openHint')}><Icon name="play" />{t('preview.open')}</button>
          {manage && (approval
            ? <button className="btn primary" disabled={busy || !dirty || !name.trim() || issues.length > 0} onClick={() => setSubmitOpen(true)}><Icon name="send" />{t('approval.submit.button')}</button>
            : <button className="btn primary" disabled={busy || !dirty || !name.trim() || issues.length > 0} onClick={() => void save()}>{dirty ? t('common.save') : t('common.saved')}</button>)}
        </div>}
      />
      <PendingBanner type="schedule" id={id}>{t('approval.pending.bannerSchedule')}</PendingBanner>
      {submitOpen && <SubmitDialog type="schedule" id={id} name={name.trim() || s.name} body={buildBody()} onClose={() => setSubmitOpen(false)}
        onSubmitted={() => { setDirty(false); setRules(null); }} />}

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <b className="wk-title">{t('schedules.week')}</b>
          <button className="btn sm ghost icon" aria-label={t('schedules.prevWeek')} onClick={() => setWeek(isoDate(addDays(monday, -7)))}><Icon name="left" /></button>
          <input type="date" aria-label={t('schedules.simulateDate')} value={week} onChange={(e) => e.target.value && setWeek(e.target.value)} style={{ width: 'auto' }} />
          <button className="btn sm ghost icon" aria-label={t('schedules.nextWeek')} onClick={() => setWeek(isoDate(addDays(monday, 7)))}><span style={{ display: 'inline-flex', transform: 'scaleX(-1)' }}><Icon name="left" /></span></button>
          <button className="btn sm" onClick={() => setWeek(today)}>{t('schedules.today')}</button>
          <select aria-label={t('schedules.timezone')} value={tz} onChange={(e) => setTz(e.target.value)} style={{ width: 'auto' }}>
            {tzs.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
        <div className="scroll">
          <div className="week" style={{ minWidth: 640 }} data-testid="week-view">
            <div className="hd" />
            {days.map(({ d }, i) => <div key={i} className="hd" style={isoDate(d) === today ? { color: 'var(--accent)' } : undefined}>{wdNames[i]} {d.day}.{d.month}.</div>)}
            <div className="hours">{hours.map((h) => <span key={h} style={{ top: `${(h / 24) * 100}%` }}>{h ? `${h}:00` : ''}</span>)}</div>
            {days.map(({ start, end }, i) => (
              <div key={i} className="day">
                {segments.filter((g) => g.until > start && g.from < end && g.playlist).map((g, j) => {
                  const from = Math.max(g.from, start);
                  const until = Math.min(g.until, end);
                  const top = ((from - start) / (end - start)) * 100;
                  const height = ((until - from) / (end - start)) * 100;
                  const conflict = conflicts.some((c) => c.winner === g.ruleId);
                  const fb = g.source === 'fallback';
                  return (
                    <div key={j} className={`blk${fb ? ' fallback' : ''}`} title={`${plName(g.playlist)}${g.ruleId ? ` · ${t('schedules.rule', { no: ruleNo(g.ruleId) })}` : ` · ${t('schedules.fallback')}`}`}
                      style={{ top: `${top}%`, height: `${height}%`, background: color(g.playlist), opacity: fb ? 0.45 : 1, outline: conflict ? '2px dashed rgba(255,255,255,.8)' : undefined, outlineOffset: -3 }}>
                      {height > 4 ? plName(g.playlist) : ''}{conflict && height > 6 ? ' ⚠' : ''}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="row small muted" style={{ marginTop: 10, flexWrap: 'wrap' }}>
          {[...new Set([...rules.map((r) => r.playlistId), ...(fallback ? [fallback] : [])])].map((pid) => (
            <span key={pid} className="row" style={{ gap: 6 }}><span className="swatch" style={{ width: 12, height: 12, borderRadius: 3, background: color(pid) }} />{plName(pid)}</span>
          ))}
          <span>{t('schedules.fallbackLegend')}</span>
        </div>
        {conflicts.length > 0 && (
          <div className="hint warn" style={{ marginTop: 10 }} role="note">
            <b>{t('schedules.conflicts')}</b>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {conflicts.map((c) => (
                <li key={c.winner}>{t('schedules.conflictText', { winner: ruleNo(c.winner), losers: [...c.losers].map((x) => ruleNo(x)).join(', ') })}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card col">
        <div className="row">
          <h2 className="grow" style={{ margin: 0 }}>{t('schedules.rules')}</h2>
          <label className="check small"><input type="checkbox" checked={expert} onChange={(e) => setExpert(e.target.checked)} />{t('schedules.expert')}</label>
        </div>
        <div className="hint">{t('schedules.ruleHint')}</div>
        {rules.map((r, i) => {
          const errs = validateRule(toEngineRule(r));
          return (
            <div key={r.key} className="rule" data-testid="rule">
              <div className="row" style={{ gridColumn: '1 / -1' }}>
                <span className="swatch" style={{ background: color(r.playlistId) }} />
                <b className="grow">{t('schedules.rule', { no: i + 1 })}</b>
                <label className="check small"><input type="checkbox" disabled={!manage} checked={r.enabled} onChange={(e) => patch(r.key, { enabled: e.target.checked })} />{t('playlists.active')}</label>
                {manage && <button className="btn sm ghost icon danger" aria-label={t('common.remove')} title={t('common.remove')} onClick={() => update((l) => l.filter((x) => x.key !== r.key))}><Icon name="trash" /></button>}
              </div>
              <Field label={t('schedules.type')}>
                <select disabled={!manage} value={r.ruleType} onChange={(e) => {
                  const ruleType = e.target.value as RuleType;
                  patch(r.key, ruleType === 'weekly' ? { ruleType, weekdays: r.weekdays ?? 31 } : { ruleType, dateFrom: r.dateFrom ?? today, dateUntil: ruleType === 'date_range' ? (r.dateUntil ?? r.dateFrom ?? today) : null });
                }}>
                  <option value="weekly">{t('schedules.weekly')}</option><option value="date_range">{t('schedules.dateRange')}</option><option value="single_date">{t('schedules.singleDate')}</option>
                </select>
              </Field>
              {r.ruleType === 'weekly' ? (
                <div className="field" style={{ gridColumn: 'span 2' }}>{t('schedules.days')}
                  <div className="days">
                    {WEEKDAY_BITS.map((bit, j) => (
                      <label key={bit}><input type="checkbox" disabled={!manage} checked={((r.weekdays ?? 0) & bit) !== 0}
                        onChange={(e) => patch(r.key, { weekdays: e.target.checked ? (r.weekdays ?? 0) | bit : (r.weekdays ?? 0) & ~bit })} /><span>{wdNames[j]}</span></label>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <Field label={r.ruleType === 'single_date' ? t('schedules.date') : t('schedules.dateFrom')}>
                    <input type="date" disabled={!manage} value={r.dateFrom ?? ''} onChange={(e) => patch(r.key, { dateFrom: e.target.value || null })} />
                  </Field>
                  {r.ruleType === 'date_range' && (
                    <Field label={t('schedules.dateUntil')}><input type="date" disabled={!manage} value={r.dateUntil ?? ''} onChange={(e) => patch(r.key, { dateUntil: e.target.value || null })} /></Field>
                  )}
                </>
              )}
              <Field label={t('schedules.timeFrom')}><input type="time" disabled={!manage} value={r.timeFrom?.slice(0, 5) ?? ''} onChange={(e) => patch(r.key, { timeFrom: e.target.value || null })} /></Field>
              <Field label={t('schedules.timeUntil')}><input type="time" disabled={!manage} value={r.timeUntil?.slice(0, 5) ?? ''} onChange={(e) => patch(r.key, { timeUntil: e.target.value || null })} /></Field>
              <Field label={t('schedules.playlist')}>
                <select disabled={!manage} value={r.playlistId} onChange={(e) => patch(r.key, { playlistId: e.target.value })}>
                  {!r.playlistId && <option value="">{t('design.choose')}</option>}
                  {playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label={t('schedules.priority')}>
                {expert ? (
                  <input type="number" min={0} max={100} disabled={!manage} value={r.priority} onChange={(e) => patch(r.key, { priority: Math.max(0, Math.min(100, Math.round(Number(e.target.value)))) })} />
                ) : (
                  <select disabled={!manage} value={PRESETS.includes(r.priority) ? r.priority : 50} onChange={(e) => patch(r.key, { priority: Number(e.target.value) })}>
                    <option value={50}>{t('schedules.prio.normal')}</option><option value={75}>{t('schedules.prio.high')}</option><option value={90}>{t('schedules.prio.veryHigh')}</option>
                  </select>
                )}
              </Field>
              {errs.length > 0 && <div className="field-error" style={{ gridColumn: '1 / -1' }}>{errs.map((x) => x.message).join(' · ')}</div>}
              {r.timeFrom && r.timeUntil && r.timeUntil <= r.timeFrom && <div className="muted small" style={{ gridColumn: '1 / -1' }}>{t('schedules.overMidnight')}</div>}
            </div>
          );
        })}
        {manage && <div><button className="btn" onClick={addRule} disabled={!playlists.length}><Icon name="plus" />{t('schedules.addRule')}</button></div>}
        <Field label={t('schedules.fallback')} hint={t('schedules.fallbackHint')}>
          <select disabled={!manage} value={fallback ?? ''} onChange={(e) => { setFallback(e.target.value || null); setDirty(true); }}>
            <option value="">{t('schedules.noFallback')}</option>
            {playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>

      <AssignmentsCard scheduleId={id} />

      {del && (
        <Confirm danger title={t('common.delete')} text={t('schedules.confirmDelete', { name: s.name })} confirmLabel={t('common.delete')} onClose={() => setDel(false)}
          onConfirm={async () => {
            try {
              await api.del(`/schedules/${id}`, { ifMatch: version });
              await qc.invalidateQueries({ queryKey: qk.schedules });
              navigate('/schedules');
            } catch (e) {
              toast.show(e instanceof ApiError && e.status === 409 ? t('schedules.inUse') : String((e as Error).message));
              setDel(false);
            }
          }} />
      )}
    </>
  );
}

